import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useContext, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { StudyLocalizationStatus } from '@axon/rosetta-sdk';
import { FirebaseAppContext } from 'libs.firebase_react';
import { AssetTypes } from 'libs.localization.common';
import { useLocalization, useLocalizationResource } from 'libs.localization.react';
import { Locale, Spinner, Text, useLocalizeMessage } from 'libs.nucleus';
import { FileInfoBox, PageHeader } from 'libs.react';
import { AssetsInfoPanel, StudyLocaleStatusProgress, TranslationsOutOfSyncBanner } from '../../components';
import { AIGeneratedBanner } from '../../components/ai_generated_banner/AI_generated_banner.component';
import { TranslationWorkbenchContext } from '../../contexts';
import { useLibraryDetails, useTranslationDetails } from '../../hooks';
export const ResourcePdfReviewView = ({ assetId, studyId }) => {
    const t = useLocalizeMessage();
    const [queryParams] = useSearchParams();
    const baseLocale = Locale.en_US;
    const { locale = baseLocale } = useParams();
    const assetType = queryParams.get('assetType');
    const { logEvent } = useContext(FirebaseAppContext);
    const { studyLocalizationStatus, resourcesAssets } = useContext(TranslationWorkbenchContext);
    const resource = Object.keys(resourcesAssets).find((key) => resourcesAssets[key].id === assetId);
    const _resourceData = resource
        ? resourcesAssets[resource]
        : {};
    const isStudyinDraft = studyLocalizationStatus !== StudyLocalizationStatus.READY;
    const asset = {
        id: assetId,
        type: assetType,
        version: '1', // @TODO figure out version
    };
    const isCompareMode = locale !== baseLocale;
    const { localizationEntry } = useLocalization(asset);
    const { version: localeVersion, status: localeStatus, localization_origin: localizationOrigin, } = localizationEntry?.supported_locales?.[locale] || {};
    const { localizationResourceEntry, isLoading: isLocalizationResourceEntryLoading } = useLocalizationResource(asset, locale, localeVersion);
    const currentBaseLocaleVersion = localizationEntry?.supported_locales?.[localizationEntry?.base_locale || Locale.en_US]?.version;
    const currentResourceBaseLocaleVersion = localizationResourceEntry?.base_locale_version;
    const isBaseLocaleInSync = currentBaseLocaleVersion === currentResourceBaseLocaleVersion;
    const baseLibraryResourceId = localizationEntry?.localization_resources?.[baseLocale]?.study_pdf?.resourceId;
    const localeLibraryResourceId = localizationResourceEntry?.library_resource_id;
    const localeLibraryTranslationId = localizationResourceEntry?.library_translation_id;
    const { data: baseLocaleData } = useLibraryDetails(baseLibraryResourceId, 'asset', true);
    const { data: localeData } = useTranslationDetails(localeLibraryResourceId, localeLibraryTranslationId, isCompareMode);
    const basePdfUrl = baseLocaleData?.data.data.blobUrl;
    const localePdfUrl = localeData?.data.data.blobUrl;
    const breadcrumbPages = useMemo(() => [
        { name: t('Translations'), path: `/studies/${studyId}/translations` },
        {
            name: _resourceData.displayName || '',
            path: `/studies/${studyId}/translations/${assetId}?assetType=${assetType}`,
        },
        { name: locale || '', path: `/studies/${studyId}/translations/${assetId}/review/${locale}` },
    ], [assetId, assetType]);
    const fileName = isCompareMode ? localeData?.data.data.data.name : baseLocaleData?.data.data.name;
    const info = [
        { title: t('Resource Name'), description: _resourceData.displayName || '' },
        {
            title: t('Type'),
            description: resource ? resourcesAssets[resource].type : '',
        },
        {
            title: t('Content type'),
            description: 'File Upload',
        },
        {
            title: t('Content'),
            description: fileName && (_jsx("div", { className: 'flex flex-wrap gap-4', children: _jsx(FileInfoBox, { name: fileName }) })),
        },
    ];
    // @TODO: Revisit this
    const handleExport = useCallback(async () => {
        if (localePdfUrl) {
            window.open(localePdfUrl, '_blank', 'noreferrer');
            logEvent('translations_screen_report_downloaded', {
                context: 'study',
                type: AssetTypes.RESOURCES_PDF,
                id: assetId,
                locale,
            });
        }
    }, [localePdfUrl]);
    const headerOptions = [
        { name: t('Export'), onClick: () => handleExport(), variant: 'primary', disabled: isStudyinDraft },
    ];
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: locale, breadcrumbPages: breadcrumbPages, options: headerOptions, LinkComponent: Link, customOptions: locale !== Locale.en_US && resource ? (_jsx(StudyLocaleStatusProgress, { asset: asset, locale: locale, assetId: assetId, isDisabled: isStudyinDraft || !isBaseLocaleInSync, disabledMessage: !isBaseLocaleInSync ? t('Translations are out of sync with the source') : '', trackingContext: { context: 'study', type: assetType } })) : (_jsx("span", {})) }), _jsx(AIGeneratedBanner, { status: localeStatus, origin: localizationOrigin }), !isBaseLocaleInSync && (_jsx("div", { className: 'mb-6', children: _jsx(TranslationsOutOfSyncBanner, {}) })), _jsx("div", { className: 'flex flex-col gap-6', children: _jsx(AssetsInfoPanel, { listItems: info, locale: Locale.en_US, title: t('Information'), version: localeVersion, localizationVersion: {
                        status: localeStatus,
                        version: Number(localeVersion),
                        origin: localizationOrigin,
                    } }) }), _jsxs("div", { className: 'mt-8', children: [isLocalizationResourceEntryLoading && _jsx(Spinner, { wrapper: 'full' }), isCompareMode && (_jsxs(Text, { className: 'mb-2', children: [baseLocale, " ", t('compared to'), " ", locale] })), _jsxs("div", { className: 'flex gap-6 w-full ', children: [_jsx("div", { className: 'flex-1 aspect-[1/1.41]', children: basePdfUrl && (_jsx("embed", { src: `${basePdfUrl}#toolbar=0&&view=FitH`, type: 'application/pdf', width: '100%', height: '100%' })) }), isCompareMode && localePdfUrl && (_jsx("div", { className: 'flex-1 aspect-[1/1.41]', children: _jsx("embed", { src: `${localePdfUrl}#toolbar=0&view=FitH`, type: 'application/pdf', width: '100%', height: '100%' }) }))] })] })] }));
};
